import type { SafetyVideoStatus } from "@unbox/api";
import { ReverseLeft } from "@untitledui/icons";
import { useCallback, useEffect, useState } from "react";
import { useConfirmNoRisks, useUndoNoRisksConfirmation } from "@/api/assurance/video";
import { AddRiskVideoModal } from "@/components/assurance/add-risk-video-modal";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { isReviewableStatus, SafetyStatusReviewPopover } from "@/components/assurance/safety-status-review-popover";
import { error as toastError } from "@/providers/ToastProvider";

const UNDO_SECONDS = 10;

const UndoButton = ({ onUndo, onExpire }: { onUndo: () => void; onExpire: () => void }) => {
  const [secondsLeft, setSecondsLeft] = useState(UNDO_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onExpire();
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, onExpire]);

  return (
    <button
      type="button"
      onClick={onUndo}
      className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-primary bg-primary py-1 pr-2 pl-2.5 text-sm font-medium text-secondary transition-colors duration-100 hover:bg-primary_hover"
    >
      <ReverseLeft className="size-3.5" />
      Undo
      <span className="tabular-nums text-quaternary">{secondsLeft}s</span>
    </button>
  );
};

/**
 * The safety status badge on a video's own page. Reviewable statuses open a popover to confirm or
 * flag the AI's outcome; a confirmation can be undone for a few seconds from here only.
 */
export const VideoSafetyStatus = ({
  videoId,
  status,
  reviewedBy,
  existingRiskIds,
}: {
  videoId: string;
  status: SafetyVideoStatus;
  reviewedBy?: { name: string; at: string | Date };
  existingRiskIds: string[];
}) => {
  const [undoOpen, setUndoOpen] = useState(false);
  const [addRiskOpen, setAddRiskOpen] = useState(false);
  const closeUndo = useCallback(() => setUndoOpen(false), []);
  const confirm = useConfirmNoRisks(videoId);
  const undo = useUndoNoRisksConfirmation(videoId);

  const badge = (withTooltip: boolean) => (
    <DetailedVideoStatusBadge status={status} showStatusIcon reviewedBy={reviewedBy} withTooltip={withTooltip} />
  );

  const handleConfirm = () => {
    confirm.mutate(undefined, {
      onSuccess: () => setUndoOpen(true),
      onError: () => toastError("Couldn't mark the video as reviewed. Please try again."),
    });
  };

  const handleUndo = () => {
    setUndoOpen(false);
    undo.mutate(undefined, {
      onError: () => toastError("Couldn't undo the review. Please try again."),
    });
  };

  return (
    <>
      {isReviewableStatus(status) && !confirm.isPending ? (
        <SafetyStatusReviewPopover
          status={status}
          badge={badge(false)}
          onConfirm={handleConfirm}
          onFlag={() => setAddRiskOpen(true)}
        />
      ) : (
        badge(true)
      )}
      {undoOpen && status === "noRisksFoundAndManuallyConfirmed" && (
        <UndoButton onUndo={handleUndo} onExpire={closeUndo} />
      )}
      <AddRiskVideoModal
        videoId={videoId}
        existingRiskIds={existingRiskIds}
        open={addRiskOpen}
        onOpenChange={setAddRiskOpen}
        onSuccess={() => setAddRiskOpen(false)}
      />
    </>
  );
};
